/**
 * RegistrarPagoAgenciaDialog — "Llegó el pago" de una reserva a cuentas por cobrar.
 *
 * El caso: la agencia pide la reserva a crédito para asegurar los cupos, y
 * más tarde, cuando su cliente le paga, manda el voucher. Eso no es una
 * corrección: es el segundo momento de una venta a crédito.
 *
 * Antes había que ir a Finanzas, elegir la agencia, teclear el monto y
 * repartirlo. Pero el caso no llega así: llega como una imagen por WhatsApp
 * que dice "esto es de la reserva tal". Por eso la acción vive en la ficha
 * de la reserva.
 *
 * Por debajo NO inventa nada: encadena tres cosas que ya existen.
 *   1. `POST /turismo/soportes/upload`               — guarda la imagen
 *   2. `POST /turismo/soportes/analizar-comprobante` — la lee con visión
 *   3. `POST /turismo/pagos-recibidos`               — registra el pago con
 *      una sola distribución, la de esta reserva, y baja el saldo
 *
 * Acepta abonos parciales: si el voucher trae menos que el saldo, se aplica
 * lo que trae y queda el resto pendiente.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, TextField, MenuItem, Alert, CircularProgress, Stack, Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PaymentsIcon from '@mui/icons-material/Payments';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

// El endpoint de pagos marca como "cruce de comisión" —asiento contable sin
// cuenta destino— cualquier pago cuyas notas mencionen cruce o comisión. Este
// es plata de verdad entrando, así que el texto evita esas dos palabras.
const NOTA = 'Voucher recibido de la agencia, aplicado desde la ficha de la reserva.';

const METODOS = [
  { valor: 'transferencia', rotulo: 'Transferencia' },
  { valor: 'nequi', rotulo: 'Nequi' },
  { valor: 'daviplata', rotulo: 'Daviplata' },
  { valor: 'consignacion', rotulo: 'Consignación' },
  { valor: 'efectivo', rotulo: 'Efectivo' },
];

const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

export default function RegistrarPagoAgenciaDialog({
  open, onClose, reserva, cuentasDestino = [], onHecho, onToast,
}) {
  const saldo = Number(reserva?.saldo_pendiente || 0);

  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [comprobanteUrl, setComprobanteUrl] = useState('');
  const [lectura, setLectura] = useState(null);   // lo que entendió la visión
  const [usos, setUsos] = useState(null);        // dónde se usó ya esta imagen
  const [monto, setMonto] = useState(String(saldo));
  const [fecha, setFecha] = useState(hoyISO());
  const [metodo, setMetodo] = useState('transferencia');
  const [cuentaId, setCuentaId] = useState('');
  const [referencia, setReferencia] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (!open) return;
    setArchivo(null); setComprobanteUrl(''); setLectura(null); setUsos(null);
    setMonto(String(Number(reserva?.saldo_pendiente || 0)));
    setFecha(hoyISO()); setMetodo('transferencia'); setCuentaId('');
    setReferencia(''); setError(null);
  }, [open, reserva?.saldo_pendiente]);

  // Subir y leer van juntos: al operador le sirve el archivo solo por lo que
  // dice, así que no tiene sentido pedirle un segundo clic para leerlo.
  const elegirArchivo = async (file) => {
    if (!file) return;
    setArchivo(file); setError(null); setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const cabeceras = { ...getAuthHeaders() };
      delete cabeceras['Content-Type'];   // el navegador pone el boundary
      const rSubir = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST', headers: cabeceras, body: fd,
      });
      const dSubir = await rSubir.json();
      if (!rSubir.ok) throw new Error(dSubir.detail || 'No se pudo subir la imagen');
      setComprobanteUrl(dSubir.url);

      // ¿Esta misma imagen ya se usó? Repetirla no es delito: un voucher de
      // un millón puede cubrir cuatro reservas de 250 mil. Lo que no puede es
      // cubrir más de lo que dice el comprobante, y para saberlo hace falta
      // cuánto se aplicó antes.
      try {
        const fd2 = new FormData();
        fd2.append('archivo', file);
        fd2.append('empresa_id', String(reserva.empresa_id));
        const rUsos = await fetch(`${API_BASE}/turismo/vouchers/usos-de-imagen`, {
          method: 'POST', headers: cabeceras, body: fd2,
        });
        if (rUsos.ok) setUsos(await rUsos.json());
      } catch { /* sin verificación, se avisa abajo */ }

      // La lectura nunca bloquea: si falla, se llenan los campos a mano.
      try {
        const rOCR = await fetch(`${API_BASE}/turismo/soportes/analizar-comprobante`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({
            url: dSubir.url, monto_esperado: saldo,
            empresa_id: String(reserva.empresa_id),
          }),
        });
        const d = await rOCR.json();
        if (rOCR.ok) {
          setLectura(d);
          if (d.monto) setMonto(String(d.monto));
          if (d.fecha_pago) setFecha(String(d.fecha_pago).slice(0, 10));
          if (d.cuenta_destino_match?.cuenta_id) setCuentaId(d.cuenta_destino_match.cuenta_id);
          if (d.referencia) setReferencia(String(d.referencia));
        }
      } catch { /* sin lectura, a mano */ }
    } catch (e) {
      setError(e.message || 'No se pudo subir la imagen');
      setArchivo(null);
    }
    setSubiendo(false);
  };

  const montoNum = Number(String(monto).replace(/[^\d.]/g, '')) || 0;
  const esAbono = montoNum > 0 && montoNum < saldo;
  const excede = montoNum > saldo;
  const necesitaCuenta = metodo !== 'efectivo';

  // Control del voucher repetido. `valorVoucher` es lo que dice el propio
  // comprobante según la lectura; `yaAplicado` lo que ya se cobró con esa
  // misma imagen. Si la suma se pasa del valor del voucher, se está cobrando
  // dos veces la misma plata.
  const yaUsada = (usos?.veces_usada || 0) > 0;
  const yaAplicado = Number(usos?.total_aplicado || 0);
  const valorVoucher = Number(lectura?.monto || 0);
  const disponible = valorVoucher > 0 ? valorVoucher - yaAplicado : null;
  const sobrepasaVoucher = disponible !== null && montoNum > disponible + 0.5;
  // Imagen repetida sin poder leer su valor: no hay con qué comparar.
  const repetidaSinValor = yaUsada && !(valorVoucher > 0);

  const puedeGuardar = !!comprobanteUrl && montoNum > 0 && !excede
    && !sobrepasaVoucher
    && !!fecha && (!necesitaCuenta || !!cuentaId) && !guardando;

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/turismo/pagos-recibidos`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          empresa_id: String(reserva.empresa_id),
          agencia_id: reserva.agencia_id,
          fecha_pago: fecha,
          monto_total: montoNum,
          metodo_pago: metodo,
          cuenta_destino_id: cuentaId || null,
          referencia: referencia || null,
          comprobante_url: comprobanteUrl,
          notas: NOTA,
          // El servidor bloquea el mismo comprobante en dos pagos distintos.
          // Acá ya se verificó contra el valor del voucher —arriba se ve
          // cuánto se aplicó antes y cuánto queda—, así que se confirma con
          // el detalle de por qué, que queda escrito en el pago.
          confirmar_comprobante_repetido: yaUsada,
          motivo_comprobante_repetido: yaUsada
            ? `Cubre varias reservas · ya aplicado ${formatPrecio(yaAplicado, reserva.moneda)}`
              + (valorVoucher > 0 ? ` de ${formatPrecio(valorVoucher, reserva.moneda)}` : '')
            : null,
          distribuciones: [{ reservacion_id: reserva.id, monto_aplicado: montoNum }],
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.message || d.detail || 'No se pudo registrar el pago');
      onToast?.success?.(esAbono
        ? `Abono de ${formatPrecio(montoNum, reserva.moneda)} aplicado. Queda ${formatPrecio(saldo - montoNum, reserva.moneda)}.`
        : 'Pago registrado. La reserva queda sin saldo.');
      onHecho?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'No se pudo registrar el pago');
    }
    setGuardando(false);
  };

  return (
    <Dialog open={!!open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Llegó el pago
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {reserva?.codigo_reserva} · saldo a cargo de la agencia:{' '}
          {formatPrecio(saldo, reserva?.moneda)}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* 1 · La imagen */}
        <Button
          component="label" variant={comprobanteUrl ? 'outlined' : 'contained'}
          startIcon={subiendo ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          disabled={subiendo || guardando}
          sx={{ textTransform: 'none', fontWeight: 700, mb: 1.5 }}
        >
          {subiendo ? 'Leyendo el voucher…'
            : comprobanteUrl ? 'Cambiar la imagen' : 'Elegir la imagen del voucher'}
          <input hidden type="file" accept="image/*,application/pdf"
            onChange={(e) => elegirArchivo(e.target.files?.[0])} />
        </Button>
        {archivo && (
          <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5 }}>
            {archivo.name}
          </Typography>
        )}

        {yaUsada && (
          <Alert
            severity={sobrepasaVoucher ? 'error' : repetidaSinValor ? 'warning' : 'info'}
            sx={{ mb: 2, fontSize: 12.5 }}
          >
            <Box sx={{ fontWeight: 700, mb: 0.5 }}>
              Este comprobante ya se usó {usos.veces_usada === 1 ? 'una vez' : `${usos.veces_usada} veces`}
            </Box>
            {usos.usos.map(u => (
              <Box key={u.voucher_id}>
                · {u.codigo_reserva || 'reserva ' + u.reservacion_id} por{' '}
                {formatPrecio(u.monto, reserva?.moneda)}
              </Box>
            ))}
            <Box sx={{ mt: 0.5 }}>
              Aplicado hasta ahora: {formatPrecio(yaAplicado, reserva?.moneda)}
              {valorVoucher > 0 && ` de ${formatPrecio(valorVoucher, reserva?.moneda)} que dice el voucher`}
            </Box>
            {sobrepasaVoucher && (
              <Box sx={{ mt: 0.5, fontWeight: 700 }}>
                Con este monto se cobraría más de lo que dice el comprobante.
                Quedan {formatPrecio(Math.max(disponible, 0), reserva?.moneda)} por aplicar.
              </Box>
            )}
            {repetidaSinValor && (
              <Box sx={{ mt: 0.5 }}>
                No se pudo leer el valor del comprobante, así que no hay con qué
                comparar. Verifica a mano que no se esté cobrando dos veces.
              </Box>
            )}
          </Alert>
        )}

        {lectura && (
          <Alert
            severity={lectura.confianza_baja ? 'warning' : 'success'}
            sx={{ mb: 2, fontSize: 12.5 }}
          >
            {lectura.confianza_baja
              ? 'La imagen se lee con dificultad — revisa los datos antes de guardar.'
              : 'Datos leídos del voucher. Revisa y confirma.'}
            {lectura.alerta_texto && (
              <Box sx={{ mt: 0.5 }}>{lectura.alerta_texto}</Box>
            )}
          </Alert>
        )}

        {/* 2 · Los datos */}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Monto del pago" fullWidth size="small" value={monto}
              onChange={(e) => setMonto(e.target.value)}
              error={excede}
              helperText={
                excede ? 'Es más que el saldo pendiente de esta reserva.'
                  : esAbono ? `Abono parcial · queda ${formatPrecio(saldo - montoNum, reserva?.moneda)}`
                    : 'Cubre el saldo completo'
              }
            />
            <TextField
              label="Fecha del pago" type="date" fullWidth size="small" value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select label="Forma de pago" fullWidth size="small" value={metodo}
              onChange={(e) => setMetodo(e.target.value)}
            >
              {METODOS.map(m => (
                <MenuItem key={m.valor} value={m.valor}>{m.rotulo}</MenuItem>
              ))}
            </TextField>
            <TextField
              select label="Cuenta a la que entró" fullWidth size="small"
              value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}
              disabled={!necesitaCuenta}
              helperText={necesitaCuenta ? 'Obligatoria para el respaldo contable' : 'No aplica en efectivo'}
            >
              <MenuItem value="">—</MenuItem>
              {cuentasDestino.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Referencia" size="small" fullWidth value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            placeholder="Nº de la transacción"
          />
        </Stack>

        {esAbono && (
          <Chip
            size="small" color="warning" sx={{ mt: 2, fontWeight: 700 }}
            label={`Quedará pendiente ${formatPrecio(saldo - montoNum, reserva?.moneda)}`}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          variant="contained" startIcon={<PaymentsIcon />}
          disabled={!puedeGuardar} onClick={guardar}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#0d9488',
                '&:hover': { bgcolor: '#0f766e' } }}
        >
          {guardando ? 'Registrando…' : 'Registrar el pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
